import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme, Button, Divider } from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import ProgressCircle from "../../components/ProgressCircle";
import { getCargamentoPorOrden } from "../../services/pedidoVenta.services";
import ModalCargamento from "../../modal/cargamento/modalCargamento";
import ModalHistorialEntregas from "../../modal/cargamento/modalHistorialEntregas";
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';


const Cargamento = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [open, setOpen] = useState(false);
  const [registroLavadora, setRegistroLavadora] = useState(null);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialPedidoId, setHistorialPedidoId] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fetchData = async () => {
    try {
      const response = await getCargamentoPorOrden();
      setOrdenes(response);
    } catch (error) {
      console.error("Error al obtener los datos del backend:", error);
    }
  };

  useEffect(() => {
    if (!open) fetchData();
  }, [open]);

  const handleCloseModal = () => {
    setOpen(false);
    fetchData();
  };

  const handleOpenModal = (pedido) => {
    if (!pedido.id_vehiculo || pedido.id_vehiculo === 1) {
      toast.error("Primero se debe asignar un vehiculo", {
        position: "top-center", autoClose: 5000, hideProgressBar: true,
        closeOnClick: true, pauseOnHover: true, draggable: true,
      });
      return;
    }
    const cargado = pedido.cantidad_cargada ?? 0;
    const asignado = pedido.cantidad_asignada ?? 0;
    if (cargado >= asignado) {
      toast.success(`Pedido ${pedido.n_pedido} completado para este viaje`, {
        position: "top-center", autoClose: 5000, hideProgressBar: true,
        closeOnClick: true, pauseOnHover: true, draggable: true,
      });
    } else {
      setRegistroLavadora(pedido);
      setOpen(true);
    }
  };

  const handleOpenHistorial = (pedido) => {
    setHistorialPedidoId(pedido.id_pedido_venta);
    setHistorialOpen(true);
  };

  return (
    <Box m="20px" sx={{
      overflowY: "auto",
      maxHeight: "90vh",
      '&::-webkit-scrollbar': { width: '8px', backgroundColor: 'transparent' },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '10px' },
      '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0)' },
    }}>
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header title="Cargamento" subtitle="Orden de Carga" />
      </Box>

      <Box display="flex" flexDirection="column" gap="20px">
        {ordenes.map((orden) => (
          <Box
            key={orden.id_orden_carga}
            backgroundColor={colors.primary[400]}
            sx={{ width: "100%", padding: "16px 20px", borderRadius: "4px" }}
          >
            {/* Order / truck header */}
            <Box display="flex" alignItems="center" gap="16px" mb={1.5}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                {orden.nro_orden ?? "Sin orden"}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.blueAccent[300] }}>
                {orden.chapa ?? "Sin vehiculo"}
              </Typography>
              {orden.motorista_nombre && (
                <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                  Motorista: {orden.motorista_nombre}
                </Typography>
              )}
              {orden.fecha_carga && (
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  {new Date(orden.fecha_carga).toLocaleDateString()}
                </Typography>
              )}
            </Box>

            <Divider sx={{ borderColor: colors.primary[300], mb: 1.5 }} />

            {/* Pedidos for this order */}
            <Box display="flex" flexDirection="column" gap="12px">
              {(orden.pedidos ?? []).map((pedido) => {
                const cargadoViaje = pedido.cantidad_cargada ?? 0;
                const cantidadViaje = pedido.cantidad_asignada ?? 1;
                const progressViaje = Math.min(1, Math.max(0, cargadoViaje / cantidadViaje));

                return (
                  <Box
                    key={pedido.id_pedido_entrega}
                    display="flex"
                    alignItems="center"
                    gap="16px"
                    sx={{
                      backgroundColor: colors.primary[500],
                      padding: "12px 16px",
                      borderRadius: "4px",
                    }}
                  >
                    {/* Trip progress */}
                    <Box display="flex" alignItems="center">
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <ProgressCircle progress={progressViaje} size="60" />
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>Viaje</Typography>
                      </Box>
                    </Box>

                    {/* Info */}
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                        {pedido.cod_produto ?? "Sin código"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        Pedido #{pedido.n_pedido}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        Este viaje: {cargadoViaje} / {cantidadViaje}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box display="flex" flexDirection="column" gap="8px">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenModal(pedido)}
                        sx={{
                          backgroundColor: "rgb(206, 220,0)",
                          color: colors.primary[900],
                          "&:hover": { backgroundColor: colors.grey[300] },
                        }}
                      >
                        Bipear
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenHistorial(pedido)}
                        sx={{
                          borderColor: colors.grey[400],
                          color: colors.grey[200],
                          "&:hover": { borderColor: colors.grey[200] },
                        }}
                      >
                        Historial
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <ModalCargamento
        open={open}
        onClose={handleCloseModal}
        registro={registroLavadora}
      />
      <ModalHistorialEntregas
        open={historialOpen}
        onClose={() => setHistorialOpen(false)}
        idPedidoVenta={historialPedidoId}
      />
    </Box>
  );
};

export default Cargamento;
